import { ConfirmationModalVariant } from "@/components/ConfirmationModal";
import { ConfirmParams } from "@/context/confirm-context";

type SignInPolicyField = "sso_enabled" | "require_sso" | "jit_provisioning_enabled";

interface PolicyChangeContentProps {
  intro?: string;
  effects: string[];
}

const PolicyChangeContent = ({ intro, effects }: PolicyChangeContentProps) => (
  <div className="space-y-3">
    {intro ? <p className="text-sm leading-relaxed text-gray-700">{intro}</p> : null}
    <div>
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">What changes</p>
      <ul className="space-y-2 text-sm leading-relaxed text-gray-700">
        {effects.map(effect => (
          <li key={effect} className="flex gap-2">
            <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
            <span>{effect}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const baseParams = (params: ConfirmParams & { requireConfirmation?: boolean }): ConfirmParams =>
  params;

export const getSignInPolicyConfirmParams = (
  field: SignInPolicyField,
  enabling: boolean,
): ConfirmParams => {
  switch (field) {
    case "sso_enabled":
      if (enabling) {
        return baseParams({
          title: "Enable SSO",
          message: (
            <PolicyChangeContent
              intro="Members will be able to sign in through your configured SAML/OIDC connection."
              effects={[
                "Email/password sign-in stays available unless Require SSO is also turned on.",
                "Existing sessions are not affected.",
              ]}
            />
          ),
          confirmText: "Enable SSO",
          cancelText: "Cancel",
          headerVariant: ConfirmationModalVariant.warning,
          saveButtonVariant: "default",
          exitButtonVariant: "outline",
        });
      }
      return baseParams({
        title: "Disable SSO",
        message: (
          <PolicyChangeContent
            intro="Members will no longer be able to sign in via your identity provider."
            effects={[
              "Only email/password sign-in will work (unless Require SSO blocks that too).",
              "Active SSO sessions may fail on the next request.",
              "Review Require SSO and JIT provisioning before disabling SSO.",
            ]}
          />
        ),
        confirmText: "Disable SSO",
        cancelText: "Cancel",
        headerVariant: ConfirmationModalVariant.destructive,
        saveButtonVariant: "destructive",
        exitButtonVariant: "outline",
        requireConfirmation: true,
        confirmationText: "disable sso",
      });
    case "require_sso":
      if (enabling) {
        return baseParams({
          title: "Require SSO",
          message: (
            <PolicyChangeContent
              intro="Email/password sign-in will be blocked for regular members."
              effects={[
                "Owners with emergency access keep email/password login if MFA and an emergency password are configured in Account Settings.",
                "Non-emergency sessions are signed out on the next request.",
                "SSO becomes the only sign-in path for regular members.",
              ]}
            />
          ),
          confirmText: "Require SSO",
          cancelText: "Cancel",
          headerVariant: ConfirmationModalVariant.warning,
          saveButtonVariant: "default",
          exitButtonVariant: "outline",
          requireConfirmation: true,
          confirmationText: "require sso",
        });
      }
      return baseParams({
        title: "Allow password sign-in",
        message: (
          <PolicyChangeContent
            intro="Members will be able to use email/password again."
            effects={[
              "SSO remains available if SSO is still enabled.",
              "Members can choose either sign-in method.",
              "This widens the attack surface compared to SSO-only access.",
            ]}
          />
        ),
        confirmText: "Allow password sign-in",
        cancelText: "Cancel",
        headerVariant: ConfirmationModalVariant.destructive,
        saveButtonVariant: "destructive",
        exitButtonVariant: "outline",
        requireConfirmation: true,
        confirmationText: "allow password login",
      });
    case "jit_provisioning_enabled":
      if (enabling) {
        return baseParams({
          title: "Enable JIT provisioning",
          message: (
            <PolicyChangeContent
              intro="New users can be created automatically on first successful SSO login."
              effects={[
                "SCIM directory sync is not required for new account creation.",
                "Allowed domains (if configured) still gate which emails can be provisioned.",
              ]}
            />
          ),
          confirmText: "Enable JIT provisioning",
          cancelText: "Cancel",
          headerVariant: ConfirmationModalVariant.warning,
          saveButtonVariant: "default",
          exitButtonVariant: "outline",
        });
      }
      return baseParams({
        title: "Disable JIT provisioning",
        message: (
          <PolicyChangeContent
            intro="New SSO users without an existing PromptLayer account will be denied."
            effects={[
              "Only users provisioned via SCIM or created manually can sign in via SSO.",
              "Existing members and sessions are not removed.",
            ]}
          />
        ),
        confirmText: "Disable JIT provisioning",
        cancelText: "Cancel",
        headerVariant: ConfirmationModalVariant.destructive,
        saveButtonVariant: "destructive",
        exitButtonVariant: "outline",
        requireConfirmation: true,
        confirmationText: "disable jit",
      });
    default:
      return {
        message: "Apply this change?",
      };
  }
};

export const getBreakGlassReassignParams = (ownerEmail: string): ConfirmParams => ({
  title: "Reassign emergency access",
  message: (
    <PolicyChangeContent
      intro={`${ownerEmail} will become the organization's emergency access Owner.`}
      effects={[
        "Only one Owner can hold emergency access at a time.",
        "The previous emergency access Owner will lose native MFA and email/password fallback when Require SSO is enabled.",
        "The new Owner must enroll MFA and set an emergency password in Account Settings.",
      ]}
    />
  ),
  confirmText: "Reassign emergency access",
  cancelText: "Cancel",
  headerVariant: ConfirmationModalVariant.warning,
  saveButtonVariant: "default",
  exitButtonVariant: "outline",
});

/** @deprecated Use getBreakGlassReassignParams for single-owner reassignment. */
export const getBreakGlassConfirmParams = (
  enabling: boolean,
  ownerEmail: string,
): ConfirmParams => ({
  title: enabling ? "Grant emergency access" : "Revoke emergency access",
  message: enabling ? (
    <PolicyChangeContent
      intro={`${ownerEmail} will keep email/password and native MFA sign-in when Require SSO is enabled.`}
      effects={[
        "They must enroll MFA and set an emergency access password in Account Settings.",
        "At least one Owner with emergency access is required before Require SSO can be turned on.",
      ]}
    />
  ) : (
    <PolicyChangeContent
      intro={`${ownerEmail} will lose emergency email/password access when Require SSO is enabled.`}
      effects={[
        "If they are the last Owner with emergency access configured, some identity policy changes may be blocked.",
      ]}
    />
  ),
  confirmText: enabling ? "Grant emergency access" : "Revoke emergency access",
  cancelText: "Cancel",
  headerVariant: enabling ? ConfirmationModalVariant.warning : ConfirmationModalVariant.destructive,
  saveButtonVariant: enabling ? "default" : "destructive",
  exitButtonVariant: "outline",
  requireConfirmation: true,
  confirmationText: enabling ? "grant emergency access" : "revoke emergency access",
});
